<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import camelcaseKeys from 'camelcase-keys';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTracking } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useVisitasStore } from 'dashboard/stores/visitas';
import VisitaAPI from 'dashboard/api/visitas';
import ImovelAPI from 'dashboard/api/imoveis';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStaticMap from 'dashboard/components-next/LaisStaticMap.vue';
import {
  channelErrorMessage,
  channelTrackingPayload,
  openResolvedChannel,
  resolveChannel,
  resolveOwnerChannel,
} from 'dashboard/helper/lais/channelRouter';
import {
  VISITA_CARD_STATUS,
  isVisitaCheckInDone,
} from 'dashboard/helper/lais/visitaPresenter';
import {
  AGENDA_QUICK_ACTIONS,
  buildIntroductionMessage,
  buildOfferMessage,
  buildWhatsAppUrl,
  canSendIntroduction,
  canSendOffer,
  canSuggestHouse,
  canTalkToOwner,
  contactName,
  contactPhone,
  propertyId,
  quickActionAttributes,
} from '../utils/agendaQuickActions';
import {
  buildHouseDetailSections,
  buildHouseSummary,
  canConfirmVisit,
  canFinishVisit,
  buildReferenceVisitSections,
  entryInfoRows,
  isTenantLivingVisit,
  normalizedRecommendationItems,
  offerInfoRows,
  propertyInfoRows,
  recommendationHouseId,
  recommendationVisitPayload,
  responsibleInfoRows,
  statusHero,
  tenantName,
  tenantPhone,
  VISIT_DETAIL_STATUS_ACTIONS,
  visitAddressLine,
  visitBusinessContext,
  visitContactId,
  visitConversationDisplayId,
  visitDateTimeLabel,
  visitImovelId,
  visitInfoRows,
  visitFeedbackRows,
  visitPropertyImage,
  visitorInfoRows,
  VISIT_DETAIL_SECTION_TITLES,
  VISIT_DETAIL_TEXT,
} from '../utils/visitDetailPresenter';
import {
  trackVisitEvent,
  VISIT_TRACKING_EVENTS,
  visitFinalizationUserAction,
} from '../utils/visitTracking';
import ExtraVisitsShortcut from '../components/ExtraVisitsShortcut.vue';
import KeyHolderInfoDialog from '../components/KeyHolderInfoDialog.vue';
import VisitAgendaInfoModal from '../components/VisitAgendaInfoModal.vue';
import VisitConfirmationModal from '../components/VisitConfirmationModal.vue';
import VisitDetailActionsPanel from '../components/VisitDetailActionsPanel.vue';
import VisitDetailRowsCard from '../components/VisitDetailRowsCard.vue';
import VisitDetailStatusPanel from '../components/VisitDetailStatusPanel.vue';
import VisitOutcomeActionsCard from '../components/VisitOutcomeActionsCard.vue';
import VisitOperationalRuntimePanel from '../components/VisitOperationalRuntimePanel.vue';
import VisitFinishChoiceModal from '../components/VisitFinishChoiceModal.vue';
import VisitHistorySummary from '../components/VisitHistorySummary.vue';
import VisitHouseInfoSections from '../components/VisitHouseInfoSections.vue';
import VisitReferenceSections from '../components/VisitReferenceSections.vue';
import VisitRescheduleModal from '../components/VisitRescheduleModal.vue';
import VisitSendActionModal from '../components/VisitSendActionModal.vue';
import VisitTenantActionDrawer from '../components/VisitTenantActionDrawer.vue';
import VisitWantCancelModal from '../components/VisitWantCancelModal.vue';

const route = useRoute();
const router = useRouter();
const store = useVisitasStore();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');
const { track } = useTracking();

const DETAIL_PAGE_LABELS = Object.freeze({
  visitFeedback: 'Feedback da visita',
  refresh: 'Atualizar',
  tryAgain: 'Tentar novamente',
  propertyData: 'Dados do imóvel',
});

const DETAIL_PANEL_ACTIONS = Object.freeze({
  SEND_INTRODUCTION: 'sendIntroduction',
  SEND_OFFER: 'sendOffer',
  OPEN_VISITOR: 'openVisitor',
  TALK_OWNER: 'talkOwner',
  TALK_VISITOR: 'talkVisitor',
  ADD_MORE: 'addMore',
  OPEN_ENTRY: 'openEntry',
});

const VISIT_DETAIL_STATUS_ACTION_MATRIX = Object.freeze({
  [VISITA_CARD_STATUS.WAITING_CONFIRMATION]: {
    statusActions: [
      VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT,
      VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT,
      VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_OWNER,
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
      VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT,
    ],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_OWNER,
      DETAIL_PANEL_ACTIONS.ADD_MORE,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.CONFIRMED]: {
    statusActions: [
      VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION,
      VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES,
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
      VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT,
    ],
    detailActions: [
      DETAIL_PANEL_ACTIONS.SEND_INTRODUCTION,
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_OWNER,
      DETAIL_PANEL_ACTIONS.ADD_MORE,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.SCHEDULED]: {
    statusActions: [
      VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION,
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
      VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT,
    ],
    detailActions: [
      DETAIL_PANEL_ACTIONS.SEND_INTRODUCTION,
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_OWNER,
      DETAIL_PANEL_ACTIONS.ADD_MORE,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.NOW]: {
    statusActions: [VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION],
    detailActions: [
      DETAIL_PANEL_ACTIONS.SEND_INTRODUCTION,
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_OWNER,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.PENDING]: {
    statusActions: [VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.DONE]: {
    statusActions: [VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK],
    detailActions: [
      DETAIL_PANEL_ACTIONS.SEND_OFFER,
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.ADD_MORE,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.UNAVAILABLE_HOURS]: {
    statusActions: [
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT,
    ],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_OWNER,
      DETAIL_PANEL_ACTIONS.ADD_MORE,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.CANCELED]: {
    statusActions: [],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.NOT_HAPPENED]: {
    statusActions: [],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
  [VISITA_CARD_STATUS.UNSUCCESSFUL]: {
    statusActions: [],
    detailActions: [
      DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
      DETAIL_PANEL_ACTIONS.TALK_VISITOR,
      DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
    ],
  },
});

const DEFAULT_VISIT_DETAIL_ACTION_MATRIX = Object.freeze({
  statusActions: [VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION],
  detailActions: [
    DETAIL_PANEL_ACTIONS.SEND_INTRODUCTION,
    DETAIL_PANEL_ACTIONS.OPEN_VISITOR,
    DETAIL_PANEL_ACTIONS.TALK_VISITOR,
    DETAIL_PANEL_ACTIONS.TALK_OWNER,
    DETAIL_PANEL_ACTIONS.OPEN_ENTRY,
  ],
});

const detailStatusAction = ({ key, label, icon, variant = 'primary' }) =>
  Object.freeze({ key, label, icon, variant });

const DETAIL_STATUS_ACTION_DEFINITIONS = Object.freeze({
  [VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT,
    label: VISIT_DETAIL_TEXT.CONFIRM_VISIT,
    icon: 'i-lucide-circle-check',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT,
    label: VISIT_DETAIL_TEXT.FINISH_VISIT,
    icon: 'i-lucide-flag',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
    label: VISIT_DETAIL_TEXT.RESCHEDULE_VISIT,
    icon: 'i-lucide-calendar-clock',
    variant: 'ghost',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT,
    label: VISIT_DETAIL_TEXT.CANCEL_VISIT,
    icon: 'i-lucide-calendar-x-2',
    variant: 'ghost',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION,
    label: VISIT_DETAIL_TEXT.SEND_PRESENTATION,
    icon: 'i-lucide-send',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK,
    label: VISIT_DETAIL_TEXT.SEND_OFFER_LINK,
    icon: 'i-lucide-file-signature',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT,
    label: VISIT_DETAIL_TEXT.TALK_TO_TENANT,
    icon: 'i-lucide-message-circle',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_OWNER]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_OWNER,
    label: VISIT_DETAIL_TEXT.TALK_TO_OWNER,
    icon: 'i-lucide-message-circle',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES,
    label: VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_BUTTON,
    icon: 'i-lucide-list-checks',
  }),
  [VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT]: detailStatusAction({
    key: VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT,
    label: VISIT_DETAIL_TEXT.RESCHEDULE_WITH_TENANT,
    icon: 'i-lucide-calendar-clock',
  }),
});

const isLoading = ref(true);
const isSaving = ref(false);
const isConfirming = ref(false);
const isCheckingIn = ref(false);
const isFinalizingOutcome = ref(false);
const isIssuingFeedbackToken = ref(false);
const isRescheduling = ref(false);
const loadError = ref(false);
const visit = ref(null);
const recommendations = ref([]);
const recommendationsLoading = ref(false);
const extraVisitsSaving = ref(false);
const showSendIntroduction = ref(false);
const showSendOffer = ref(false);
const showWantCancel = ref(false);
const showReschedule = ref(false);
const showConfirm = ref(false);
const showConfirmSuccess = ref(false);
const showFinishChoice = ref(false);
const showOutcomeDialog = ref(false);
const showAgendaInfo = ref(false);
const agendaInfoKind = ref('responsible');
const showKeyHolder = ref(false);
const tenantActionMode = ref('');
const deepLinkActionConsumed = ref(false);
const entryHistoryVisits = ref([]);
const entryHistoryMeta = ref({});
const entryHistoryLoaded = ref(false);
const entryHistoryLoading = ref(false);
const entryInformationPayload = ref({});
const trackedBriefingWidgetVisitCode = ref(null);
const feedbackLink = ref('');
const outcomeError = ref('');
const outcomeIdempotencyKey = ref('');
const ENTRY_HISTORY_PAGE_SIZE = 10;

const createOutcomeIdempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `visit-outcome:${Date.now()}:${Math.random().toString(36).slice(2)}`;

const hasEntryValue = value => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
};

const compactEntryObject = object =>
  Object.entries(object || {}).reduce((acc, [key, value]) => {
    if (hasEntryValue(value)) acc[key] = value;
    return acc;
  }, {});

const entryInformationDetailValue = (payload, key) => {
  const details = Array.isArray(payload?.details) ? payload.details : [];
  return details.find(item => item?.key === key)?.value;
};

const nativeEntryAccessFromPayload = payload => {
  const source = payload || {};
  return compactEntryObject({
    accessLabel: source.accessLabel,
    accessKey: source.accessKey || source.access_key,
    helper: source.helper,
    responsibleName:
      source.responsibleName ||
      entryInformationDetailValue(source, 'responsible_name'),
    responsiblePhone:
      source.responsiblePhone ||
      entryInformationDetailValue(source, 'responsible_phone'),
    responsibleAddress:
      source.responsibleAddress ||
      entryInformationDetailValue(source, 'responsible_address'),
    accessCode: entryInformationDetailValue(source, 'password'),
    keyTypeLabel: entryInformationDetailValue(source, 'key_type'),
    keyStatus: source.entranceStatus,
    instructions:
      source.instructions ||
      entryInformationDetailValue(source, 'instructions') ||
      source.helper,
    details: source.details,
    keysWithAgent: source.keysWithAgent,
  });
};

const mergeVisitEntryInformation = (source = {}, payload = {}) => {
  const entryAccess = nativeEntryAccessFromPayload(payload);
  if (!Object.keys(entryAccess).length) return source;

  const imovel = source.imovel || {};
  const imovelAttrs =
    imovel.additionalAttributes || imovel.additional_attributes || {};
  const currentEntryAccess =
    imovel.entryAccess ||
    imovel.entry_access ||
    imovelAttrs.entryAccess ||
    imovelAttrs.entry_access ||
    {};

  return {
    ...source,
    imovel: {
      ...imovel,
      entryAccess: {
        ...currentEntryAccess,
        ...entryAccess,
      },
      additionalAttributes: {
        ...imovelAttrs,
        entryAccess: {
          ...currentEntryAccess,
          ...entryAccess,
        },
      },
    },
  };
};

const normalizedVisit = computed(() => visit.value || {});
const visitWithEntryInformation = computed(() =>
  mergeVisitEntryInformation(
    normalizedVisit.value,
    entryInformationPayload.value
  )
);
const hero = computed(() => statusHero(normalizedVisit.value));
const visitCardStatus = computed(() => hero.value?.cardStatus || '');
const actionMatrix = computed(
  () =>
    VISIT_DETAIL_STATUS_ACTION_MATRIX[visitCardStatus.value] ||
    DEFAULT_VISIT_DETAIL_ACTION_MATRIX
);
const allowedStatusActions = computed(
  () => new Set(actionMatrix.value.statusActions || [])
);
const detailPanelActionKeys = computed(() => actionMatrix.value.detailActions);
const visitId = computed(() => route.params.id);
const visitTrackingCode = computed(
  () =>
    normalizedVisit.value?.visitCode ||
    normalizedVisit.value?.visit_code ||
    normalizedVisit.value?.id
);
const imageSrc = computed(() => visitPropertyImage(normalizedVisit.value));
const address = computed(() => visitAddressLine(normalizedVisit.value));
const dateText = computed(() => visitDateTimeLabel(normalizedVisit.value));
const contactId = computed(() => visitContactId(normalizedVisit.value));
const imovelId = computed(() => visitImovelId(normalizedVisit.value));
const canOpenVisitor = computed(() => Boolean(contactId.value));
const checkInDone = computed(() =>
  Boolean(
    normalizedVisit.value?.checkinDone ||
      normalizedVisit.value?.checkin_done ||
      isVisitaCheckInDone(normalizedVisit.value)
  )
);
const visitOutcome = computed(
  () =>
    normalizedVisit.value?.visitOutcome || normalizedVisit.value?.visit_outcome
);
const canRegisterCheckIn = computed(
  () =>
    Boolean(visit.value?.id) &&
    !checkInDone.value &&
    [
      VISITA_CARD_STATUS.CONFIRMED,
      VISITA_CARD_STATUS.NOW,
      VISITA_CARD_STATUS.PENDING,
    ].includes(visitCardStatus.value)
);
const canRegisterOutcome = computed(
  () =>
    Boolean(visit.value?.id) &&
    !visitOutcome.value &&
    canFinishVisit(normalizedVisit.value)
);
const embeddedHistoryVisits = computed(
  () => normalizedVisit.value?.imovel?.visitHistory || []
);
const historyVisits = computed(() =>
  entryHistoryLoaded.value
    ? entryHistoryVisits.value
    : embeddedHistoryVisits.value
);
const entryHistoryHasMore = computed(() =>
  Boolean(
    entryHistoryMeta.value?.hasNextPage || entryHistoryMeta.value?.has_next_page
  )
);
const entryHistoryNextPage = computed(
  () =>
    entryHistoryMeta.value?.nextPage ||
    entryHistoryMeta.value?.next_page ||
    Number(entryHistoryMeta.value?.currentPage || 1) + 1
);
const introductionPreview = computed(() =>
  buildIntroductionMessage({
    visit: normalizedVisit.value,
    agentName: currentUser.value?.name || 'Imudei',
    companyName: currentAccount.value?.name || 'Imudei',
  })
);
const offerPreview = computed(() =>
  buildOfferMessage({
    accountId: accountId.value,
    visit: normalizedVisit.value,
    origin: typeof window === 'undefined' ? '' : window.location.origin,
  })
);
const keyHolderLink = computed(() =>
  imovelId.value
    ? `${window.location.origin}${frontendURL(
        `accounts/${accountId.value}/imoveis/${imovelId.value}/edit`
      )}`
    : ''
);
const visitorProfileUrl = computed(() =>
  contactId.value
    ? frontendURL(
        `accounts/${accountId.value}/visitas/visitante/${contactId.value}`,
        {
          business_context: visitBusinessContext(normalizedVisit.value),
          backTo: route.fullPath,
        }
      )
    : ''
);
const conversationId = computed(() =>
  visitConversationDisplayId(normalizedVisit.value)
);
const inboxId = computed(
  () =>
    normalizedVisit.value?.inboxId ||
    normalizedVisit.value?.inbox_id ||
    normalizedVisit.value?.contact?.inboxId ||
    normalizedVisit.value?.contact?.inbox_id
);
const canTalkToVisitor = computed(() =>
  Boolean(
    contactPhone(normalizedVisit.value) ||
      conversationId.value ||
      (contactId.value && inboxId.value)
  )
);
const statusActionRequirements = computed(() => ({
  [VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT]: canConfirmVisit(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT]: canFinishVisit(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT]: Boolean(visit.value?.id),
  [VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT]: Boolean(visit.value?.id),
  [VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION]: canSendIntroduction(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK]: canSendOffer(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT]: isTenantLivingVisit(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES]: isTenantLivingVisit(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT]: isTenantLivingVisit(
    normalizedVisit.value
  ),
  [VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_OWNER]: canTalkToOwner(
    normalizedVisit.value
  ),
}));
const isStatusActionAllowed = actionKey =>
  allowedStatusActions.value.has(actionKey);
const isStatusActionReady = actionKey =>
  isStatusActionAllowed(actionKey) &&
  statusActionRequirements.value[actionKey] !== false;
const decorateStatusAction = action => ({
  ...action,
  disabled: !isStatusActionReady(action.key),
});
const isContextualStatusActionVisible = actionKey => {
  if (
    [
      VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT,
      VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES,
      VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT,
    ].includes(actionKey)
  ) {
    return isTenantLivingVisit(normalizedVisit.value);
  }

  return true;
};
const permittedStatusActions = actions =>
  (actions || [])
    .filter(action => isStatusActionAllowed(action.key))
    .filter(action => isContextualStatusActionVisible(action.key))
    .map(decorateStatusAction);
const matrixStatusActions = computed(() =>
  (actionMatrix.value.statusActions || [])
    .map(actionKey => DETAIL_STATUS_ACTION_DEFINITIONS[actionKey])
    .filter(Boolean)
);
const statusHeroForActions = computed(() => {
  const currentHero = hero.value || {};
  const actions = permittedStatusActions(matrixStatusActions.value);
  const primaryAction =
    actions.find(action => action.key === currentHero.primaryAction?.key) ||
    actions.find(action => !action.disabled) ||
    null;
  const orderedActions = primaryAction
    ? [
        primaryAction,
        ...actions.filter(action => action.key !== primaryAction.key),
      ]
    : actions;
  const floatingAction =
    currentHero.floatingAction &&
    isStatusActionReady(currentHero.floatingAction.key)
      ? currentHero.floatingAction
      : null;

  return {
    ...currentHero,
    primaryAction,
    secondaryActions: orderedActions.filter(
      action => action.key !== primaryAction?.key
    ),
    actions: orderedActions,
    floatingAction,
  };
});
const statusFloatingAction = computed(
  () => statusHeroForActions.value?.floatingAction || null
);
const finishImageSrc = computed(() => imageSrc.value);
const agendaInfoTitle = computed(
  () =>
    ({
      responsible: VISIT_DETAIL_SECTION_TITLES.RESPONSIBLE,
      learnMore: 'Saiba mais sobre esta visita',
      keyHolder: VISIT_DETAIL_SECTION_TITLES.ENTRY,
    })[agendaInfoKind.value] || 'Informações da agenda'
);
const referenceSections = computed(() =>
  buildReferenceVisitSections(normalizedVisit.value, {
    companyName: currentAccount.value?.name || 'Imudei',
    agentName: currentUser.value?.name || normalizedVisit.value?.user?.name,
  })
);
const hasBriefingReferenceSection = computed(() =>
  referenceSections.value.some(section => section.key === 'briefing')
);
const houseSummary = computed(() => buildHouseSummary(normalizedVisit.value));
const houseDetailSections = computed(() =>
  buildHouseDetailSections(normalizedVisit.value, {
    companyName: currentAccount.value?.name || 'Imudei',
  })
);
const tenantActionName = computed(() => tenantName(normalizedVisit.value));
const tenantActionPhone = computed(() => tenantPhone(normalizedVisit.value));
const tenantActionModeForDrawer = computed(() => {
  if (tenantActionMode.value === 'best-practices') return 'best-practices';
  if (tenantActionMode.value === 'reschedule') return 'reschedule';
  return 'talk';
});
const tenantMessagePreview = computed(() =>
  [
    `Olá, ${tenantActionName.value}!`,
    `Me chamo ${currentUser.value?.name || 'Imudei'} e faço parte do time de corretores do ${
      currentAccount.value?.name || 'Imudei'
    }.`,
    `Tenho um cliente interessado em conhecer o imóvel ${address.value} em ${dateText.value}.`,
    tenantActionMode.value === 'reschedule'
      ? 'Em qual outro dia você consegue nos receber?'
      : 'Você consegue nos receber?',
  ].join('\n\n')
);

const trackDetail = (event, action, extra = {}) =>
  trackVisitEvent({
    track,
    event,
    visit: normalizedVisit.value,
    action,
    extra,
  });

const trackStatusAction = actionKey => {
  trackDetail(VISIT_TRACKING_EVENTS.DETAIL_ACTION_CLICKED, actionKey, {
    action_origin: 'visit_status_v2',
    status_card: hero.value?.cardStatus || null,
  });
};

const trackBriefingWidgetViewed = () => {
  if (
    isLoading.value ||
    loadError.value ||
    !hasBriefingReferenceSection.value ||
    !visitTrackingCode.value ||
    trackedBriefingWidgetVisitCode.value === visitTrackingCode.value
  ) {
    return;
  }

  trackedBriefingWidgetVisitCode.value = visitTrackingCode.value;
  trackDetail(
    VISIT_TRACKING_EVENTS.BRIEFING_WIDGET_VIEWED,
    'briefing_widget_viewed',
    {
      action_origin: 'visit_detail_reference_sections',
      user_id: currentUser.value?.id,
    }
  );
};

const loadRecommendations = async () => {
  if (!visit.value?.id) return;
  recommendationsLoading.value = true;
  try {
    const payload = await store.recommendations(visit.value.id, { limit: 4 });
    recommendations.value = normalizedRecommendationItems(payload);
    trackDetail(VISIT_TRACKING_EVENTS.MORE_VISITS_VIEWED, 'view', {
      suggestion_count: recommendations.value.length,
      suggestion_house_id: recommendations.value.map(recommendationHouseId),
    });
  } catch {
    recommendations.value = [];
  } finally {
    recommendationsLoading.value = false;
  }
};

const normalizeEntryHistoryPayload = payload =>
  camelcaseKeys(payload || {}, { deep: true });

const mergeEntryHistoryVisits = (currentVisits, nextVisits) => {
  const visitedIds = new Set();
  return [...currentVisits, ...nextVisits].filter(item => {
    const key = item?.id || item?.schedulingId || item?.scheduling_id;
    if (!key) return true;
    if (visitedIds.has(key)) return false;
    visitedIds.add(key);
    return true;
  });
};

const resetEntryHistory = () => {
  entryHistoryVisits.value = [];
  entryHistoryMeta.value = {};
  entryHistoryLoaded.value = false;
  entryHistoryLoading.value = false;
  entryInformationPayload.value = {};
};

const loadEntryHistory = async ({ page = 1, replace = false } = {}) => {
  if (!imovelId.value || entryHistoryLoading.value) return;

  entryHistoryLoading.value = true;
  try {
    const { data } = await ImovelAPI.entryInformation(imovelId.value, {
      page,
      pageSize: ENTRY_HISTORY_PAGE_SIZE,
    });
    const payload = normalizeEntryHistoryPayload(data?.payload || data);
    const visits = Array.isArray(payload.visitHistory)
      ? payload.visitHistory
      : [];

    entryInformationPayload.value = payload;
    entryHistoryVisits.value = replace
      ? visits
      : mergeEntryHistoryVisits(entryHistoryVisits.value, visits);
    entryHistoryMeta.value = payload.visitHistoryMeta || {};
    entryHistoryLoaded.value = true;
  } catch {
    if (!entryHistoryLoaded.value) {
      entryHistoryVisits.value = [];
      entryHistoryMeta.value = {};
    }
  } finally {
    entryHistoryLoading.value = false;
  }
};

const loadMoreEntryHistory = () =>
  loadEntryHistory({ page: entryHistoryNextPage.value });

const loadVisit = async () => {
  isLoading.value = true;
  loadError.value = false;
  resetEntryHistory();
  try {
    const record = await store.show(visitId.value);
    visit.value = camelcaseKeys(record, { deep: true });
    trackDetail(VISIT_TRACKING_EVENTS.DETAIL_VIEWED, 'view');
    await loadEntryHistory({ page: 1, replace: true });
    await loadRecommendations();
  } catch {
    loadError.value = true;
    visit.value = null;
  } finally {
    isLoading.value = false;
  }
};

const refreshVisit = async () => {
  if (!visit.value?.id) return;
  const record = await store.show(visit.value.id);
  visit.value = camelcaseKeys(record, { deep: true });
};

const operationalActionError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.errors?.[0] ||
  fallback;

const registerCheckIn = async () => {
  if (!visit.value?.id || checkInDone.value || !canRegisterCheckIn.value) {
    return;
  }

  isCheckingIn.value = true;
  try {
    await VisitaAPI.checkIn(visit.value.id);
    await refreshVisit();
    useAlert('Presença registrada com sucesso.');
  } catch (error) {
    useAlert(
      operationalActionError(error, 'Não foi possível registrar a presença.')
    );
  } finally {
    isCheckingIn.value = false;
  }
};

const finalizeCommercialOutcome = async payload => {
  if (!visit.value?.id || !canRegisterOutcome.value) return;

  isFinalizingOutcome.value = true;
  outcomeError.value = '';
  outcomeIdempotencyKey.value ||= createOutcomeIdempotencyKey();
  try {
    await VisitaAPI.finalizeOutcome(visit.value.id, {
      ...payload,
      idempotency_key: outcomeIdempotencyKey.value,
    });
    outcomeIdempotencyKey.value = '';
    showOutcomeDialog.value = false;
    await refreshVisit();
    useAlert('Resultado da visita registrado com sucesso.');
  } catch (error) {
    outcomeError.value = operationalActionError(
      error,
      'Não foi possível registrar o resultado da visita.'
    );
  } finally {
    isFinalizingOutcome.value = false;
  }
};

const copyFeedbackLink = async () => {
  if (!feedbackLink.value) return;

  try {
    await navigator.clipboard.writeText(feedbackLink.value);
    useAlert('Link de feedback copiado.');
  } catch {
    useAlert('Não foi possível copiar o link de feedback.');
  }
};

const issueFeedbackToken = async () => {
  if (!visit.value?.id) return;

  isIssuingFeedbackToken.value = true;
  try {
    const response = await VisitaAPI.issueFeedbackToken(visit.value.id, {
      actor_role: 'visitor',
    });
    const path = response?.data?.payload?.path || response?.data?.path;
    if (!path) throw new Error('feedback_path_missing');

    feedbackLink.value = path.startsWith('http')
      ? path
      : `${window.location.origin}${path}`;
    await copyFeedbackLink();
  } catch (error) {
    useAlert(
      operationalActionError(
        error,
        'Não foi possível gerar o link de feedback.'
      )
    );
  } finally {
    isIssuingFeedbackToken.value = false;
  }
};

const goBack = () => {
  router.push(frontendURL(`accounts/${accountId.value}/visitas`));
};

const openChannel = (resolvedChannel, successMessage) => {
  if (resolvedChannel.channel === 'error') {
    useAlert(channelErrorMessage(resolvedChannel));
    return false;
  }

  const opened = openResolvedChannel({ resolvedChannel, router });
  useAlert(opened ? successMessage : channelErrorMessage(resolvedChannel));
  return opened;
};

const visitorChannel = ({ message, preferDialer = false } = {}) =>
  resolveChannel({
    accountId: accountId.value,
    phone: contactPhone(normalizedVisit.value),
    text: message,
    preferDialer,
    conversationId: conversationId.value,
    contactId: contactId.value,
    inboxId: inboxId.value,
  });

const markQuickAction = async (action, extra = {}) => {
  if (!visit.value?.id) return;
  await store.update({
    id: visit.value.id,
    additional_attributes: quickActionAttributes({ action, extra }),
  });
  await refreshVisit();
};

const confirmVisit = async () => {
  if (!visit.value?.id) return;
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT)) {
    useAlert('Confirmação não disponível para o status atual da visita.');
    return;
  }

  isConfirming.value = true;
  trackDetail(VISIT_TRACKING_EVENTS.CONFIRMATION_BUTTON_CLICKED, 'confirm', {
    click_origin: 'visit_detail',
    is_visit_assurance: true,
  });
  try {
    await store.update({
      id: visit.value.id,
      status: 'confirmada',
      additional_attributes: quickActionAttributes({
        action: AGENDA_QUICK_ACTIONS.CONFIRM,
      }),
    });
    showConfirmSuccess.value = true;
    useAlert('Pronto, visita confirmada!');
    trackDetail(VISIT_TRACKING_EVENTS.VISIT_CONFIRMED, 'confirm');
    await refreshVisit();
  } catch {
    useAlert('Não foi possível confirmar a visita.');
  } finally {
    isConfirming.value = false;
  }
};

const cancelVisit = async (payload = {}) => {
  if (!visit.value?.id) return;
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT)) {
    useAlert('Cancelamento não disponível para o status atual da visita.');
    return;
  }

  const reasonMessage = String(payload.reasonMessage || '').trim();
  if (!reasonMessage) {
    useAlert('Informe o motivo do cancelamento.');
    return;
  }

  isSaving.value = true;
  trackDetail(VISIT_TRACKING_EVENTS.CANCEL_CONTINUE_CLICKED, 'cancel');
  try {
    await store.update({
      id: visit.value.id,
      status: 'cancelada',
      cancellation_reason: 'outro',
      cancellation_responsible: 'corretor',
      additional_attributes: {
        visit_cancellation: {
          reason: payload.reason || 'OTHER',
          responsible: payload.responsible || 'AGENT',
          reason_message: reasonMessage,
          source: 'visit_detail',
        },
      },
    });
    showWantCancel.value = false;
    useAlert('A visita foi cancelada.');
    await refreshVisit();
  } catch {
    useAlert('Infelizmente não foi possível cancelar a visita.');
  } finally {
    isSaving.value = false;
  }
};

const rescheduleVisit = () => {
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT)) {
    useAlert('Reagendamento não disponível para o status atual da visita.');
    return;
  }

  trackDetail(VISIT_TRACKING_EVENTS.RESCHEDULE_CLICKED, 'reschedule');
  showWantCancel.value = false;
  showReschedule.value = true;
};

const openTenantAction = mode => {
  const actionByMode = {
    talk: VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT,
    'best-practices': VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES,
    reschedule: VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT,
  };

  if (!isStatusActionReady(actionByMode[mode])) {
    useAlert('Ação de inquilino indisponível para esta visita.');
    return;
  }

  tenantActionMode.value = mode;

  const event =
    mode === 'best-practices'
      ? VISIT_TRACKING_EVENTS.TENANT_BEST_PRACTICES_CLICKED
      : VISIT_TRACKING_EVENTS.TALK_TO_TENANT_CLICKED;

  trackDetail(event, mode, {
    has_tenant_contact: Boolean(tenantActionPhone.value),
    is_tenant_living: isTenantLivingVisit(normalizedVisit.value),
  });
};

const closeTenantAction = () => {
  tenantActionMode.value = '';
};

const openTenantWhatsApp = () => {
  const href = buildWhatsAppUrl({
    phone: tenantActionPhone.value,
    message: tenantMessagePreview.value,
  });

  if (!href) {
    useAlert('Telefone do inquilino não informado.');
    return;
  }

  window.open(href, '_blank', 'noopener,noreferrer');
  trackDetail(VISIT_TRACKING_EVENTS.WHATSAPP_LIVING_TENANT, 'tenant_whatsapp', {
    has_tenant_contact: true,
    tenant_action_mode: tenantActionMode.value,
  });
};

const callTenant = () => {
  if (!tenantActionPhone.value) {
    useAlert('Telefone do inquilino não informado.');
    return;
  }

  window.open(`tel:${tenantActionPhone.value}`, '_blank');
  trackDetail(VISIT_TRACKING_EVENTS.CALL_LIVING_TENANT, 'tenant_call', {
    has_tenant_contact: true,
    tenant_action_mode: tenantActionMode.value,
  });
};

const continueTenantReschedule = () => {
  if (
    !isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT)
  ) {
    useAlert('Reagendamento com inquilino indisponível para esta visita.');
    return;
  }

  trackDetail(
    VISIT_TRACKING_EVENTS.TENANT_CONTINUE_RESCHEDULE,
    'tenant_continue_reschedule',
    {
      has_tenant_contact: Boolean(tenantActionPhone.value),
    }
  );
  closeTenantAction();
  rescheduleVisit();
};

const submitReschedule = async payload => {
  if (!visit.value?.id) return;
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT)) {
    useAlert('Reagendamento não disponível para o status atual da visita.');
    return;
  }
  if (!payload?.scheduled_at || !String(payload?.reason || '').trim()) {
    useAlert('Informe nova data, horário e motivo do reagendamento.');
    return;
  }

  isRescheduling.value = true;
  try {
    const record = await store.reschedule(visit.value.id, payload);
    visit.value = camelcaseKeys(record, { deep: true });
    showReschedule.value = false;
    useAlert('Visita reagendada com sucesso.');
    trackDetail(VISIT_TRACKING_EVENTS.VISIT_RESCHEDULED, 'reschedule');
    await loadRecommendations();
  } catch {
    useAlert('Não foi possível reagendar a visita.');
  } finally {
    isRescheduling.value = false;
  }
};

const finishVisit = outcome => {
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT)) {
    useAlert('Finalização não disponível para o status atual da visita.');
    return;
  }

  showFinishChoice.value = false;
  trackDetail(VISIT_TRACKING_EVENTS.FINALIZATION_DIALOG_ACTION, 'finish', {
    user_action: visitFinalizationUserAction(outcome),
  });
  router.push(
    frontendURL(
      `accounts/${accountId.value}/visitas/${visit.value.id}/finalizar?outcome=${outcome}`
    )
  );
};

const sendIntroduction = async () => {
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION)) {
    useAlert('Apresentação indisponível para o status atual da visita.');
    return;
  }

  const resolvedChannel = visitorChannel({
    message: introductionPreview.value,
  });
  const opened = openChannel(resolvedChannel, 'Apresentação aberta.');
  trackDetail(
    VISIT_TRACKING_EVENTS.DETAIL_ACTION_CLICKED,
    'send_introduction',
    {
      ...channelTrackingPayload(resolvedChannel),
      opened,
    }
  );
  if (opened) {
    await markQuickAction(AGENDA_QUICK_ACTIONS.INTRODUCTION, {
      ...channelTrackingPayload(resolvedChannel),
    });
    showSendIntroduction.value = false;
  }
};

const sendOffer = async () => {
  if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK)) {
    useAlert('Envio de proposta indisponível para o status atual da visita.');
    return;
  }

  let offerRecord = null;
  try {
    const { data } = await PropertyOffersAPI.sendOfferLink({
      payload: {
        houseId: propertyId(normalizedVisit.value),
        visitorId: contactId.value,
      },
    });
    offerRecord = data;
  } catch {
    useAlert('Não foi possível registrar o envio da proposta.');
    trackDetail(VISIT_TRACKING_EVENTS.OFFER_LINK_FAILURE, 'send_offer');
    return;
  }

  const resolvedChannel = visitorChannel({ message: offerPreview.value });
  const opened = openChannel(
    resolvedChannel,
    offerRecord?.already_sent
      ? 'Reenvio de proposta aberto.'
      : 'Link de proposta aberto.'
  );
  trackDetail(
    opened
      ? VISIT_TRACKING_EVENTS.OFFER_LINK_SUCCESS
      : VISIT_TRACKING_EVENTS.OFFER_LINK_FAILURE,
    'send_offer',
    {
      ...channelTrackingPayload(resolvedChannel),
      opened,
      offer_link_event_id: offerRecord?.event_id,
    }
  );
  if (opened) {
    await markQuickAction(AGENDA_QUICK_ACTIONS.OFFER, {
      ...channelTrackingPayload(resolvedChannel),
      imovel_id: propertyId(normalizedVisit.value),
      contact_id: contactId.value,
      offer_link_event_id: offerRecord?.event_id,
      offer_link_deep_link: offerRecord?.deep_link,
    });
    showSendOffer.value = false;
  }
};

const talkToVisitor = () => {
  if (!canTalkToVisitor.value) {
    useAlert('Canal do visitante não informado.');
    return;
  }

  const resolvedChannel = visitorChannel({
    message: `Olá${contactName(normalizedVisit.value) ? `, ${contactName(normalizedVisit.value)}` : ''}!`,
  });
  const opened = openChannel(resolvedChannel, 'Conversa do cliente aberta.');
  trackDetail(VISIT_TRACKING_EVENTS.SEND_MESSAGE_CLICKED, 'talk_visitor', {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
};

const talkToOwner = async () => {
  if (!canTalkToOwner(normalizedVisit.value)) {
    agendaInfoKind.value = 'responsible';
    showAgendaInfo.value = true;
    return;
  }

  const resolvedChannel = resolveOwnerChannel({
    accountId: accountId.value,
    visit: normalizedVisit.value,
    accountConfig: currentAccount.value?.settings || currentAccount.value,
    text: `Olá! Quero alinhar a visita do cliente ${contactName(
      normalizedVisit.value
    )} para ${address.value}.`,
  });
  const opened = openChannel(
    resolvedChannel,
    'Conversa com proprietário aberta.'
  );
  trackDetail(VISIT_TRACKING_EVENTS.OWNER_CONTACT_CLICKED, 'talk_owner', {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  if (opened) {
    await markQuickAction(AGENDA_QUICK_ACTIONS.OWNER, {
      ...channelTrackingPayload(resolvedChannel),
    });
  }
};

const handleStatusAction = actionKey => {
  if (!isStatusActionReady(actionKey)) {
    useAlert('Ação indisponível para o status atual da visita.');
    return;
  }

  trackStatusAction(actionKey);

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT) {
    showConfirm.value = true;
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT) {
    showFinishChoice.value = true;
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT) {
    rescheduleVisit();
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT) {
    showWantCancel.value = true;
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION) {
    showSendIntroduction.value = true;
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK) {
    showSendOffer.value = true;
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_TENANT) {
    openTenantAction('talk');
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.TENANT_BEST_PRACTICES) {
    openTenantAction('best-practices');
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_WITH_TENANT) {
    openTenantAction('reschedule');
    return;
  }

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.TALK_TO_OWNER) {
    talkToOwner();
  }
};

const openVisitorProfile = () => {
  if (!visitorProfileUrl.value) {
    useAlert('Cliente não informado para esta visita.');
    return;
  }
  trackDetail(VISIT_TRACKING_EVENTS.CLIENT_PROFILE_CLICKED, 'open_visitor');
  router.push(visitorProfileUrl.value);
};

const openListing = item => {
  const id = recommendationHouseId(item) || imovelId.value;
  if (!id) return;
  trackDetail(VISIT_TRACKING_EVENTS.LISTING_CLICKED, 'open_listing', {
    house_id: id,
    shortcut: Boolean(item),
  });
  router.push(frontendURL(`accounts/${accountId.value}/imoveis/${id}`));
};

const handleHouseInfoAction = (actionName, action = {}) => {
  if (actionName === 'openListing') {
    openListing();
    return;
  }

  const actionKey = action.key || actionName;
  if (actionKey === 'go-to-listing' && action.href) {
    trackDetail(VISIT_TRACKING_EVENTS.LISTING_CLICKED, 'open_listing', {
      house_id: imovelId.value,
      shortcut: false,
    });
    return;
  }

  if (actionKey === 'third-party-info') {
    trackDetail(
      VISIT_TRACKING_EVENTS.THIRD_PARTY_INFO_CLICKED,
      'third_party_info',
      {
        action_origin: 'house_info',
        house_info_action: actionKey,
        is_third_party_agent: true,
      }
    );
  }

  trackDetail(VISIT_TRACKING_EVENTS.DETAIL_ACTION_CLICKED, 'house_info', {
    house_info_action: actionKey,
  });
};

const handleHouseCodeCopied = code => {
  useAlert(VISIT_DETAIL_TEXT.CODE_COPIED);
  trackDetail(VISIT_TRACKING_EVENTS.DETAIL_ACTION_CLICKED, 'copy_house_id', {
    copied_house_id: code,
  });
};

const extraVisitTrackingData = selectedRecommendations => ({
  suggestion_count: selectedRecommendations.length,
  suggestion_house_id: selectedRecommendations.map(recommendationHouseId),
});

const openExtraVisitsIntent = selectedRecommendations => {
  trackDetail(
    VISIT_TRACKING_EVENTS.MORE_VISITS_INTENT_CLICKED,
    'open_extra_review',
    extraVisitTrackingData(selectedRecommendations)
  );
};

const openExtraVisitsReview = selectedRecommendations => {
  trackDetail(
    VISIT_TRACKING_EVENTS.MORE_VISITS_REVIEW_VIEWED,
    'review_extra',
    extraVisitTrackingData(selectedRecommendations)
  );
};

const scheduleExtraVisits = async selectedRecommendations => {
  if (!selectedRecommendations.length) return;
  extraVisitsSaving.value = true;
  trackDetail(
    VISIT_TRACKING_EVENTS.MORE_VISITS_SCHEDULING_CLICKED,
    'schedule_extra',
    extraVisitTrackingData(selectedRecommendations)
  );
  try {
    const payloads = selectedRecommendations.map(recommendation =>
      recommendationVisitPayload({
        visit: normalizedVisit.value,
        recommendation,
      })
    );
    await VisitaAPI.requestVisitBatch({
      visitorId: contactId.value,
      businessContext: visitBusinessContext(normalizedVisit.value),
      origin: 'Corretores',
      onBehalfOfRole: 'DEMAND',
      sourceVisitId: normalizedVisit.value?.id,
      visits: payloads,
    });
    useAlert(VISIT_DETAIL_TEXT.EXTRA_VISITS_SUCCESS);
    trackDetail(
      VISIT_TRACKING_EVENTS.MORE_VISITS_SUCCESS_VIEWED,
      'schedule_extra',
      extraVisitTrackingData(selectedRecommendations)
    );
    await loadRecommendations();
  } catch {
    useAlert(VISIT_DETAIL_TEXT.EXTRA_VISITS_ERROR);
    trackDetail(
      VISIT_TRACKING_EVENTS.MORE_VISITS_ERROR_VIEWED,
      'schedule_extra',
      extraVisitTrackingData(selectedRecommendations)
    );
  } finally {
    extraVisitsSaving.value = false;
  }
};

const openEntryInfo = () => {
  showAgendaInfo.value = false;
  showKeyHolder.value = true;
};

const updateEntry = () => {
  if (!imovelId.value) return;
  router.push(
    frontendURL(`accounts/${accountId.value}/imoveis/${imovelId.value}/edit`)
  );
};

const addMoreVisits = () => {
  trackDetail(VISIT_TRACKING_EVENTS.NEW_VISIT_CLICKED, 'new_visit', {
    action_origin: 'visit_detail',
    has_house_context: Boolean(imovelId.value),
  });
  const query = imovelId.value
    ? `?context=schedule&imovel_id=${imovelId.value}`
    : '';
  router.push(frontendURL(`accounts/${accountId.value}/visitas/new${query}`));
};

const handleReferenceSectionAction = (actionName, action = {}) => {
  if ((action.key || actionName) === 'briefing') {
    trackDetail(
      VISIT_TRACKING_EVENTS.BRIEFING_BUTTON_CLICKED,
      'briefing_button_clicked',
      {
        action_origin: 'visit_detail_reference_sections',
        has_briefing_url: Boolean(action.href),
        user_id: currentUser.value?.id,
      }
    );
    return;
  }

  if (actionName === 'talkOwner') {
    talkToOwner();
    return;
  }

  if (actionName === 'openResponsibleInfo') {
    agendaInfoKind.value = 'responsible';
    showAgendaInfo.value = true;
  }
};

const applyDeepLinkAction = () => {
  if (deepLinkActionConsumed.value || isLoading.value || loadError.value) {
    return;
  }

  if (!visit.value?.id) return;

  if (route.query.openCancelVisit === 'true') {
    if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT)) {
      deepLinkActionConsumed.value = true;
      return;
    }

    showWantCancel.value = true;
    deepLinkActionConsumed.value = true;
    return;
  }

  if (route.query.openFinishVisit === 'true') {
    if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT)) {
      deepLinkActionConsumed.value = true;
      return;
    }

    showFinishChoice.value = true;
    deepLinkActionConsumed.value = true;
    return;
  }

  if (route.query.openRescheduleVisit === 'true') {
    if (!isStatusActionReady(VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT)) {
      deepLinkActionConsumed.value = true;
      return;
    }

    deepLinkActionConsumed.value = true;
    rescheduleVisit();
  }
};

onMounted(loadVisit);

watch(
  () => route.params.id,
  () => {
    deepLinkActionConsumed.value = false;
    loadVisit();
  }
);

watch(
  [visitTrackingCode, hasBriefingReferenceSection, isLoading, loadError],
  trackBriefingWidgetViewed,
  { immediate: true }
);

watch(
  [
    isLoading,
    loadError,
    () => route.query.openCancelVisit,
    () => route.query.openFinishVisit,
    () => route.query.openRescheduleVisit,
  ],
  applyDeepLinkAction,
  { immediate: true }
);
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-screen bg-lais-bg font-redHatText text-lais-text"
  >
    <LaisHeader
      :title="VISIT_DETAIL_TEXT.PAGE_TITLE"
      :subtitle="address"
      show-back
      sticky
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          @click="loadVisit"
        >
          {{ DETAIL_PAGE_LABELS.refresh }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <section
      class="mx-auto grid w-full max-w-6xl gap-4 px-4 py-5 sm:px-6 lg:px-10"
    >
      <div
        v-if="isLoading"
        class="grid gap-4 rounded-lais border border-lais-border bg-white p-6 shadow-lais-card"
      >
        <div class="h-5 w-48 animate-pulse rounded bg-lais-bg-soft" />
        <div class="h-24 animate-pulse rounded bg-lais-bg-soft" />
        <p class="text-[14px] text-lais-text-muted">
          {{ VISIT_DETAIL_TEXT.LOADING }}
        </p>
      </div>

      <div
        v-else-if="loadError"
        class="grid justify-items-center gap-3 rounded-lais border border-lais-border bg-white px-4 py-12 text-center shadow-lais-card"
      >
        <span class="i-lucide-circle-alert size-9 text-lais-danger" />
        <h2 class="text-[18px] font-semibold text-lais-text">
          {{ VISIT_DETAIL_TEXT.LOAD_ERROR_TITLE }}
        </h2>
        <p class="max-w-md text-[14px] leading-relaxed text-lais-text-body">
          {{ VISIT_DETAIL_TEXT.LOAD_ERROR_DESCRIPTION }}
        </p>
        <div class="flex flex-wrap justify-center gap-2">
          <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadVisit">
            {{ DETAIL_PAGE_LABELS.tryAgain }}
          </LaisPrimaryButton>
          <LaisPrimaryButton variant="ghost" @click="goBack">
            {{ VISIT_DETAIL_TEXT.BACK_TO_AGENDA }}
          </LaisPrimaryButton>
        </div>
      </div>

      <template v-else>
        <VisitDetailStatusPanel
          :hero="statusHeroForActions"
          :can-confirm="canConfirmVisit(normalizedVisit)"
          :can-finish="canFinishVisit(normalizedVisit)"
          @action="handleStatusAction"
        />

        <LaisCard v-if="imageSrc" density="flush">
          <LaisStaticMap
            :src="imageSrc"
            :alt="normalizedVisit?.imovel?.titulo || address"
            aspect-class="aspect-[16/6]"
          />
        </LaisCard>

        <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section class="grid min-w-0 content-start gap-4">
            <VisitDetailActionsPanel
              :allowed-actions="detailPanelActionKeys"
              :can-send-introduction="canSendIntroduction(normalizedVisit)"
              :can-send-offer="canSendOffer(normalizedVisit)"
              :can-talk-to-owner="canTalkToOwner(normalizedVisit)"
              :can-talk-to-visitor="canTalkToVisitor"
              :can-open-visitor="canOpenVisitor"
              :can-add-more="canSuggestHouse(normalizedVisit)"
              @send-introduction="
                handleStatusAction(
                  VISIT_DETAIL_STATUS_ACTIONS.SEND_PRESENTATION
                )
              "
              @send-offer="
                handleStatusAction(VISIT_DETAIL_STATUS_ACTIONS.SEND_OFFER_LINK)
              "
              @open-visitor="openVisitorProfile"
              @talk-owner="talkToOwner"
              @talk-visitor="talkToVisitor"
              @add-more="addMoreVisits"
              @open-entry="openEntryInfo"
            />

            <VisitDetailRowsCard
              :title="VISIT_DETAIL_SECTION_TITLES.VISIT"
              icon="i-lucide-calendar-clock"
              :rows="visitInfoRows(normalizedVisit)"
            />
            <VisitDetailRowsCard
              v-if="visitFeedbackRows(normalizedVisit).length"
              :title="DETAIL_PAGE_LABELS.visitFeedback"
              icon="i-lucide-message-square-text"
              :rows="visitFeedbackRows(normalizedVisit)"
            />
            <VisitDetailRowsCard
              v-if="propertyInfoRows(normalizedVisit).length"
              :title="DETAIL_PAGE_LABELS.propertyData"
              icon="i-lucide-map-pinned"
              :rows="propertyInfoRows(normalizedVisit)"
            />
            <VisitHouseInfoSections
              :summary="houseSummary"
              :sections="houseDetailSections"
              :title="VISIT_DETAIL_SECTION_TITLES.HOUSE"
              :details-title="VISIT_DETAIL_SECTION_TITLES.HOUSE_DETAILS"
              @action="handleHouseInfoAction"
              @copied="handleHouseCodeCopied"
            />
            <VisitDetailRowsCard
              :title="VISIT_DETAIL_SECTION_TITLES.ENTRY"
              icon="i-lucide-key-round"
              :rows="entryInfoRows(visitWithEntryInformation)"
            />
            <VisitHistorySummary
              :visit="visitWithEntryInformation"
              :visits="historyVisits"
              :owner-present="Boolean(normalizedVisit?.ownerFeedback)"
              :server-has-more="entryHistoryHasMore"
              :loading-more="entryHistoryLoading"
              @load-more="loadMoreEntryHistory"
            />
          </section>

          <aside class="grid min-w-0 content-start gap-4">
            <VisitDetailRowsCard
              :title="VISIT_DETAIL_SECTION_TITLES.VISITOR"
              icon="i-lucide-user-round"
              :rows="visitorInfoRows(normalizedVisit)"
            />
            <VisitDetailRowsCard
              :title="VISIT_DETAIL_SECTION_TITLES.RESPONSIBLE"
              icon="i-lucide-user-round-cog"
              :rows="responsibleInfoRows(visitWithEntryInformation)"
            />
            <VisitOperationalRuntimePanel :visit="normalizedVisit" />
            <VisitOutcomeActionsCard
              :visit="normalizedVisit"
              :can-check-in="canRegisterCheckIn"
              :can-finalize-outcome="canRegisterOutcome"
              :check-in-saving="isCheckingIn"
              :outcome-saving="isFinalizingOutcome"
              :outcome-error="outcomeError"
              :outcome-dialog-open="showOutcomeDialog"
              :feedback-link="feedbackLink"
              :feedback-token-saving="isIssuingFeedbackToken"
              @check-in="registerCheckIn"
              @update:outcome-dialog-open="showOutcomeDialog = $event"
              @finalize-outcome="finalizeCommercialOutcome"
              @issue-feedback-token="issueFeedbackToken"
              @copy-feedback-link="copyFeedbackLink"
            />
            <VisitReferenceSections
              :sections="referenceSections"
              @action="handleReferenceSectionAction"
            />
            <VisitDetailRowsCard
              :title="VISIT_DETAIL_SECTION_TITLES.OFFER"
              icon="i-lucide-file-signature"
              :rows="offerInfoRows(normalizedVisit)"
            />
            <ExtraVisitsShortcut
              :visit="normalizedVisit"
              :recommendations="recommendations"
              :loading="recommendationsLoading"
              :saving="extraVisitsSaving"
              :visitor-id="contactId"
              @confirm="scheduleExtraVisits"
              @open-profile="openVisitorProfile"
              @open-listing="openListing"
              @reload="loadRecommendations"
              @intent-click="openExtraVisitsIntent"
              @review-open="openExtraVisitsReview"
            />
          </aside>
        </div>
      </template>
    </section>

    <VisitSendActionModal
      :show="showSendIntroduction"
      :title="VISIT_DETAIL_TEXT.SEND_PRESENTATION"
      description="Envie a apresentação antes da visita para confirmar encontro, horário e endereço com o cliente."
      confirm-label="Enviar apresentação"
      :preview="introductionPreview"
      @close="showSendIntroduction = false"
      @confirm="sendIntroduction"
    />

    <VisitSendActionModal
      :show="showSendOffer"
      :title="VISIT_DETAIL_TEXT.SEND_OFFER_LINK"
      description="Encaminhe o link de proposta para acelerar a próxima etapa depois da visita."
      confirm-label="Enviar link de proposta"
      :preview="offerPreview"
      @close="showSendOffer = false"
      @confirm="sendOffer"
    />

    <VisitWantCancelModal
      :show="showWantCancel"
      :active-visit="normalizedVisit"
      :quick-visit-date-text="dateText"
      :has-visitor-check-in="checkInDone"
      @close="showWantCancel = false"
      @reschedule="rescheduleVisit"
      @continue="cancelVisit"
    />

    <VisitRescheduleModal
      :show="showReschedule"
      :active-visit="normalizedVisit"
      :current-date-text="dateText"
      :loading="isRescheduling"
      @close="showReschedule = false"
      @submit="submitReschedule"
    />

    <VisitConfirmationModal
      :show="showConfirm"
      :success="showConfirmSuccess"
      :visit="normalizedVisit"
      :loading="isConfirming"
      @close="showConfirm = false"
      @action="showConfirm = false"
      @confirm="confirmVisit"
      @edit-entry="openEntryInfo"
    />

    <VisitFinishChoiceModal
      :show="showFinishChoice"
      :candidate="normalizedVisit"
      :image-src="finishImageSrc"
      :address="address"
      :date-text="dateText"
      @close="showFinishChoice = false"
      @finish="finishVisit"
    />

    <VisitAgendaInfoModal
      :show="showAgendaInfo"
      :title="agendaInfoTitle"
      :kind="agendaInfoKind"
      :can-update-entry="Boolean(imovelId)"
      @close="showAgendaInfo = false"
      @update-entry="updateEntry"
      @add-more="addMoreVisits"
    />

    <KeyHolderInfoDialog
      :show="showKeyHolder"
      :link="keyHolderLink"
      @close="showKeyHolder = false"
    />

    <VisitTenantActionDrawer
      :show="Boolean(tenantActionMode)"
      :mode="tenantActionModeForDrawer"
      :tenant-name="tenantActionName"
      :tenant-phone="tenantActionPhone"
      :business-context="visitBusinessContext(normalizedVisit)"
      @close="closeTenantAction"
      @whatsapp="openTenantWhatsApp"
      @call="callTenant"
      @continue-reschedule="continueTenantReschedule"
    />

    <div
      v-if="!isLoading && !loadError && statusFloatingAction"
      class="pointer-events-none fixed inset-x-4 bottom-4 z-30 flex justify-end sm:inset-x-auto sm:right-6"
    >
      <LaisPrimaryButton
        class="pointer-events-auto min-h-12 shadow-lais-pop"
        :icon="statusFloatingAction.icon"
        @click="handleStatusAction(statusFloatingAction.key)"
      >
        {{ statusFloatingAction.label }}
      </LaisPrimaryButton>
    </div>
  </LaisGamificationSurface>
</template>
